import { useId, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, LANGUAGE_PATHS } from '@/config/brand';
import { MARK } from '@/config/brand-mark';
import type { LanguageCode } from '@/i18n';

/**
 * The mark: a head peeking up from behind the frame's edge ("shy"), cut off by
 * it ("cut"). Drawn from the same geometry as the favicon and app icons; the
 * eyes blink when the logo is hovered (not with reduced motion).
 */
export function LogoMark({ size = 28 }: { readonly size?: number }) {
  const clip = useId();
  const { head, eyes, eye, radius } = MARK;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${MARK.size} ${MARK.size}`} aria-hidden className="shrink-0">
      <defs>
        <clipPath id={clip}>
          <rect width={MARK.size} height={MARK.size} rx={radius} />
        </clipPath>
      </defs>
      <rect width={MARK.size} height={MARK.size} rx={radius} fill="var(--accent)" />
      <g clipPath={`url(#${clip})`}>
        <circle cx={head.cx} cy={head.cy} r={head.r} fill="var(--accent-ink)" />
        {eyes.map((e) => (
          <ellipse key={e.cx} cx={e.cx} cy={e.cy} rx={eye.rx} ry={eye.ry} fill="var(--accent)" className="logo-eye" style={{ transformOrigin: `${e.cx}px ${e.cy}px` }} />
        ))}
      </g>
    </svg>
  );
}

const Wordmark = () => (
  <>
    <LogoMark />
    <span className="text-[17px] font-semibold tracking-[-0.03em] text-fg max-[349px]:sr-only">{BRAND.name.toLowerCase()}</span>
  </>
);

export function Logo() {
  return (
    <span className="group/logo inline-flex shrink-0 items-center gap-2.5">
      <Wordmark />
    </span>
  );
}

/**
 * The logo as the way home, like the content pages' header. Already on a clean
 * home page it only scrolls up instead of loading the page again.
 */
export function HomeLogo() {
  const { t, i18n } = useTranslation();
  const home = LANGUAGE_PATHS[i18n.language as LanguageCode] ?? LANGUAGE_PATHS.en;
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    if (location.pathname !== home || location.search !== '') return;
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  return (
    <a
      href={home}
      onClick={onClick}
      aria-label={t('nav.home', { name: BRAND.name })}
      className="group/logo -m-1.5 inline-flex shrink-0 cursor-pointer items-center gap-2.5 rounded-xl p-1.5 outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <Wordmark />
    </a>
  );
}
